/**
 * PPT-specific context providers for the ReminderCollector.
 * 
 * These functions implement the context provider interfaces declared in
 * agent-runtime's services.ts. They are wired in App.tsx via configureAgentServices().
 */

import { getDeckRegistry } from "../../../../services/tracking/deckRegistry";
import { LiveTrackingStorage } from "../../../../services/tracking/liveTrackingStorage";
import { backendClient } from "../../../../services/communication/backendClient";
import type {
    DeckRegistryContext,
    DeckInfoItem,
    SlideDescription,
    PresentationOverviewContext,
    StyleSignature,
    LayoutSummary,
    DeckStyleRegistry,
} from "@carousel-office/agent-runtime/llm/reminder/types";

const storage = new LiveTrackingStorage();

export async function collectDeckRegistryContext(anchoredDocumentId?: string): Promise<DeckRegistryContext | undefined> {
    console.log('[ReminderCollector] 📚 Collecting deck registry context...');
    
    const registry = getDeckRegistry();
    await registry.refresh();
    
    const allDecks = registry.getAllDecks();
    if (allDecks.length === 0) {
        console.log('[ReminderCollector] ❌ No decks in registry');
        return undefined;
    }
    
    await storage.initialize();
    
    const decks: DeckInfoItem[] = [];
    const deckLogs: Array<{ index: number; title: string; descriptionsCount: number; totalSlides: number }> = [];
    
    for (let index = 0; index < allDecks.length; index++) {
        const info = allDecks[index];
        
        const snapshots = await storage.getDeckSnapshots(info.id);
        const slideDescriptions: SlideDescription[] = snapshots
            .sort((a, b) => a.slideIndex - b.slideIndex)
            .map(s => ({
                slideIndex: s.slideIndex + 1,
                slideId: s.slideId,
                shortDescription: s.shortDescription,
            }));
        
        const descCount = slideDescriptions.filter(s => s.shortDescription).length;
        deckLogs.push({
            index,
            title: info.title,
            descriptionsCount: descCount,
            totalSlides: info.totalSlides
        });
        
        decks.push({
            index,
            id: info.id,
            title: info.title,
            slidesReady: info.slidesReady,
            slidesIndexing: info.slidesIndexing,
            slidesRetrying: info.slidesRetrying,
            slidesFailed: info.slidesFailed,
            totalSlides: info.totalSlides,
            status: info.status,
            isCurrent: info.isCurrent,
            slideDescriptions,
        });
    }
    
    console.log('[ReminderCollector] ✅ Found decks in registry:', {
        totalDecks: allDecks.length,
        decks: deckLogs
    });
    
    const currentDocumentId = anchoredDocumentId || registry.getCurrentDocumentId();
    if (anchoredDocumentId) {
        console.log('[ReminderCollector] Using anchored currentDocumentId from conversation:', anchoredDocumentId);
    }
    
    return {
        decks,
        currentDocumentId,
        totalDecks: decks.length,
    };
}

export async function collectPresentationOverview(documentId: string): Promise<PresentationOverviewContext | undefined> {
    console.log(`[ReminderCollector] 📊 Collecting presentation overview for deck ${documentId}...`);
    
    // 1. Try full overview first (from completed processing)
    try {
        const fullResponse = await backendClient.getPresentationOverview(documentId);
        
        if (
            fullResponse.success &&
            fullResponse.overview &&
            (
                fullResponse.overview.top_style_signatures.length > 0 ||
                fullResponse.layouts.length > 0 ||
                (Array.isArray(fullResponse.style_registry?.defsXml) && fullResponse.style_registry.defsXml.length > 0)
            )
        ) {
            console.log('[ReminderCollector] ✅ Got full presentation overview');
            
            const topStyles: StyleSignature[] = fullResponse.overview.top_style_signatures.map((sig: any) => ({
                hash: sig.hash,
                count: sig.count,
                usage_percentage: sig.usage_percentage,
                style: (sig.style as Record<string, unknown>) || {},
            }));
            
            const layouts: LayoutSummary[] = fullResponse.layouts.map((layout: any) => ({
                layoutId: layout.layout_id,
                layoutName: layout.layout_id,
                description: layout.description,
                masterId: layout.master_id,
                masterName: layout.master_name,
                count: fullResponse.overview!.layout_distribution[layout.layout_id],
            }));
            
            const styleRegistry = (fullResponse.style_registry || undefined) as DeckStyleRegistry | undefined;
            
            return {
                totalSlides: fullResponse.overview.total_slides,
                status: 'full',
                topStyles,
                layouts,
                styleRegistry,
            };
        }
    } catch (error) {
        console.log('[ReminderCollector] Full overview not available, trying quick fallback:', error);
    }
    
    // 2. Fallback to quick overview using IndexedDB snapshots
    await storage.initialize();
    const snapshots = await storage.getDeckSnapshots(documentId);
    
    if (snapshots.length > 0) {
        const snapshotsWithPptx = snapshots.filter(s => s.pptxBase64);
        
        if (snapshotsWithPptx.length > 0) {
            console.log(`[ReminderCollector] Trying quick overview with ${snapshotsWithPptx.length} slides with PPTX data`);
            
            try {
                const quickResponse = await backendClient.getQuickPresentationOverview(
                    documentId,
                    snapshotsWithPptx.map(s => ({
                        slideIndex: s.slideIndex,
                        pptxBase64: s.pptxBase64!,
                        layoutId: s.layoutId,
                        layoutName: s.layoutName,
                    }))
                );
                
                if (quickResponse.success && quickResponse.top_styles.length > 0) {
                    console.log('[ReminderCollector] ✅ Got quick presentation overview');
                    
                    const topStyles: StyleSignature[] = quickResponse.top_styles.map((sig: any) => ({
                        hash: sig.hash,
                        count: sig.count,
                        usage_percentage: sig.usage_percentage,
                        style: (sig.style as Record<string, unknown>) || {},
                    }));
                    
                    const layouts: LayoutSummary[] = quickResponse.layouts.map((layout: any) => ({
                        layoutId: layout.layout_id,
                        layoutName: layout.layout_name,
                        count: layout.count,
                    }));
                    
                    return {
                        totalSlides: quickResponse.total_slides,
                        status: 'quick',
                        topStyles,
                        layouts,
                    };
                }
            } catch (quickError) {
                console.warn('[ReminderCollector] Quick overview failed:', quickError);
            }
        }
    }
    
    // 3. Final fallback: slide count + layout names from snapshots
    console.log('[ReminderCollector] Using minimal fallback (slide count + layout names)');
    
    const layoutCounts = new Map<string, { layoutId: string; layoutName: string; count: number }>();
    for (const snapshot of snapshots) {
        if (snapshot.layoutId) {
            const key = snapshot.layoutId;
            const existing = layoutCounts.get(key);
            if (existing) {
                existing.count++;
            } else {
                layoutCounts.set(key, {
                    layoutId: snapshot.layoutId,
                    layoutName: snapshot.layoutName || 'Unknown',
                    count: 1,
                });
            }
        }
    }
    
    const layouts: LayoutSummary[] = Array.from(layoutCounts.values());
    
    return {
        totalSlides: snapshots.length,
        status: 'unavailable',
        topStyles: [],
        layouts,
    };
}
